"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { PointerEvent as ReactPointerEvent } from "react"

import ProductPreview from "@modules/products/components/product-preview"

type ProductCarouselProps = {
  products: any[]
  region: any
  productRows?: 1 | 2
}

type DragState = {
  active: boolean
  pointerId: number | null
  startX: number
  scrollStart: number
  moved: boolean
}

type CarouselRowControls = {
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  isDragging: boolean
  progress: number
  handlePointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void
  handlePointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void
  endDragging: (event: ReactPointerEvent<HTMLDivElement>) => void
}

const useRowCarousel = (itemCount: number): CarouselRowControls => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dragStateRef = useRef<DragState>({
    active: false,
    pointerId: null,
    startX: 0,
    scrollStart: 0,
    moved: false,
  })

  const [isDragging, setIsDragging] = useState(false)
  const [progress, setProgress] = useState(1)

  const updateProgress = useCallback(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = container
    const maxScroll = scrollWidth - clientWidth

    if (maxScroll <= 0) {
      setProgress(1)
      return
    }

    const ratio = Math.min(1, (scrollLeft + clientWidth) / scrollWidth)
    setProgress(ratio)
  }, [])

  useEffect(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    container.style.scrollBehavior = "smooth"
    updateProgress()

    const handleScroll = () => updateProgress()
    container.addEventListener("scroll", handleScroll, { passive: true })

    let resizeObserver: ResizeObserver | undefined
    const handleWindowResize = () => updateProgress()

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => updateProgress())
      resizeObserver.observe(container)
    } else {
      window.addEventListener("resize", handleWindowResize)
    }

    return () => {
      container.removeEventListener("scroll", handleScroll)
      if (resizeObserver) {
        resizeObserver.disconnect()
      } else {
        window.removeEventListener("resize", handleWindowResize)
      }
    }
  }, [itemCount, updateProgress])

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== undefined && event.button !== 0) {
        return
      }

      if (event.target !== event.currentTarget) {
        return
      }

      const container = containerRef.current

      if (!container) {
        return
      }

      container.style.scrollBehavior = "auto"

      dragStateRef.current.active = true
      dragStateRef.current.pointerId = event.pointerId
      dragStateRef.current.startX = event.clientX
      dragStateRef.current.scrollStart = container.scrollLeft
      dragStateRef.current.moved = false

      setIsDragging(true)
      container.setPointerCapture(event.pointerId)
    },
    []
  )

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const container = containerRef.current
      const state = dragStateRef.current

      if (!container || !state.active || state.pointerId !== event.pointerId) {
        return
      }

      event.preventDefault()

      const delta = event.clientX - state.startX

      if (!state.moved && Math.abs(delta) > 5) {
        state.moved = true
      }

      container.scrollLeft = state.scrollStart - delta
      updateProgress()
    },
    [updateProgress]
  )

  const endDragging = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const container = containerRef.current
      const state = dragStateRef.current

      if (!container || !state.active || state.pointerId !== event.pointerId) {
        return
      }

      state.active = false
      state.pointerId = null
      state.startX = 0
      state.scrollStart = container.scrollLeft

      setIsDragging(false)

      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId)
      }

      container.style.scrollBehavior = "smooth"
      updateProgress()
      state.moved = false
    },
    [updateProgress]
  )

  return {
    containerRef,
    isDragging,
    progress,
    handlePointerDown,
    handlePointerMove,
    endDragging,
  }
}

const ProductCarousel = ({
  products,
  region,
  productRows = 2,
}: ProductCarouselProps) => {
  const normalizedProducts = Array.isArray(products) ? products : []
  const hasProducts = normalizedProducts.length > 0
  const showTwoRows = productRows === 2

  const topRowProducts = useMemo(
    () =>
      showTwoRows
        ? normalizedProducts.filter((_, index) => index % 2 === 0)
        : normalizedProducts,
    [normalizedProducts, showTwoRows]
  )
  const bottomRowProducts = useMemo(
    () =>
      showTwoRows
        ? normalizedProducts.filter((_, index) => index % 2 === 1)
        : [],
    [normalizedProducts, showTwoRows]
  )

  const topRow = useRowCarousel(topRowProducts.length)
  const bottomRow = useRowCarousel(bottomRowProducts.length)

  if (!hasProducts) {
    return null
  }

  const rows = (
    showTwoRows
      ? [
          {
            label: "Productos destacados fila superior",
            products: topRowProducts,
            controls: topRow,
          },
          {
            label: "Productos destacados fila inferior",
            products: bottomRowProducts,
            controls: bottomRow,
          },
        ]
      : [
          {
            label: "Productos destacados",
            products: topRowProducts,
            controls: topRow,
          },
        ]
  ).filter((row) => row.products.length > 0)

  return (
    <div className="relative space-y-6">
      {rows.map(({ label, products: rowProducts, controls }, rowIndex) => (
        <div key={label} className="relative">
          <div
            ref={controls.containerRef}
            className={`flex gap-4 overflow-x-auto no-scrollbar select-none p-2 -m-2 ${
              controls.isDragging ? "cursor-grabbing" : "cursor-grab"
            }`}
            onPointerDown={controls.handlePointerDown}
            onPointerMove={controls.handlePointerMove}
            onPointerUp={controls.endDragging}
            onPointerLeave={controls.endDragging}
            onPointerCancel={controls.endDragging}
            role="list"
            aria-label={label}
          >
            {rowProducts.map((product: any, index: number) => {
              const key =
                product.id ??
                product._id ??
                (product.slug
                  ? `${product.slug}-row-${rowIndex}-${index}`
                  : `product-row-${rowIndex}-${index}`)

              return (
                <div
                  key={key}
                  className="flex-shrink-0 basis-[65%] sm:basis-[55%] md:basis-[35%] lg:basis-[25%]"
                  role="listitem"
                >
                  <ProductPreview
                    product={product}
                    region={region}
                    isFeatured
                  />
                </div>
              )
            })}
          </div>
          <div className="mt-4 h-[3px] w-full bg-black/10 md:hidden">
            <div
              className="h-full w-full origin-left bg-black transition-transform duration-200 ease-out"
              style={{ transform: `scaleX(${controls.progress})` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default ProductCarousel
